import React, { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import { useSelector } from "react-redux";
import PwaInitAlert from "./shared/alerts/pwa-init";
import PwaUpdateAlert from "./shared/alerts/pwa-update";
import Modals from "./shared/modals";
import Splash from "./shared/splash";
import moment from "moment-jalaali";
import NotFound from "./notfound/notfound";
import Bidding from "./bidding";
import Wallet from "./wallet";
import NotificationList from "./NotificationList";

moment.loadPersian({ dialect: "persian-modern" });

const Bookings = lazy(() => import("./bookings"));
const Timeslots = lazy(() => import("./timeslots"));
const Schedule = lazy(() => import("./schedule"));
const EditSchedule = lazy(() => import("./schedule/edit"));
const NewSchedule = lazy(() => import("./schedule/new"));
const OfficeProfile = lazy(() => import("./office-profile"));
const Offices = lazy(() => import("./offices"));
const ContactUs = lazy(() => import("./contact-us"));
const AuthLoading = lazy(() => import("./auth-loading"));
const Layouts = lazy(() => import("./layouts"));
const Login = lazy(() => import("./auth/login"));
const TransferBanner = lazy(() => import("./transfer-banner"));
const Forgot = lazy(() => import("./auth/forgot"));
const ForgotVerify = lazy(() => import("./auth/forgot-verify"));
const ForgotReset = lazy(() => import("./auth/forgot-reset"));
const Reports = lazy(() => import("./reports"));
const Offline = lazy(() => import("./offline"));
const Tickets = lazy(() => import("./tickets"));
const NewTicket = lazy(() => import("./tickets/new-ticket"));
const TicketDetail = lazy(() => import("./tickets/ticket-detail"));
const Comments = lazy(() => import("./comments"));
const Package = lazy(() => import("./packages"));
const PackageVerification = lazy(() => import("./packages/package-verfication"));
const BiddingVerification = lazy(() => import("./bidding/bidding-verfication"));
const Ranks = lazy(() => import("./ranks"));
const EditInfo = lazy(() => import("./editInfo"));
const DoctorsOffice = lazy(() => import("./doctorsOffice"));
const DoctorOfficeDetails = lazy(() => import("./doctorOfficeDetails"));
const DashboardHome = lazy(() => import("./dashboard/home"));
const NotificationItem = lazy(() => import("./NotificationItem"));
const Dashboard = lazy(() => import("./dashboard"));
const Landing = lazy(() => import("./landing"));

function App() {
  const [visibleInitAlert, setVisibleInitAlert] = useState(true);
  const [visibleUpdateAlert, setVisibleUpdateAlert] = useState(true);

  const isServiceWorkerInitialized = useSelector((state) => state.pwa.serviceWorkerInitialized);

  const isServiceWorkerUpdated = useSelector((state) => state.pwa.serviceWorkerUpdated);

  const serviceWorkerRegistration = useSelector((state) => state.pwa.serviceWorkerRegistration);

  const onDismissInitAlert = () => setVisibleInitAlert(false);
  const onDismissUpdateAlert = () => setVisibleUpdateAlert(false);

  const forceSWupdate = () => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then(function (registrations) {
        for (let registration of registrations) {
          registration.update();
        }
      });
    }
  };

  const updateServiceWorker = () => {
    const registrationWaiting = serviceWorkerRegistration.waiting;
    if (registrationWaiting) {
      registrationWaiting.postMessage({ type: "SKIP_WAITING" });
      registrationWaiting.addEventListener("statechange", (e) => {
        if (e.target.state === "activated") {
          forceSWupdate();
          window.location.href = "/";
          // change location to home page
          // older code was below, which will reload the same page.
          // window.location.reload();
        }
      });
    }
    onDismissUpdateAlert();
  };

  //this useEffect check if there is update then call updateServiceWorker automatically
  useEffect(() => {
    isServiceWorkerUpdated && updateServiceWorker();
  }, []);

  return (
    <Router>
      {isServiceWorkerInitialized && (
        <PwaInitAlert visibleInitAlert={visibleInitAlert} onDismissInitAlert={onDismissInitAlert} />
      )}
      {isServiceWorkerUpdated && (
        <PwaUpdateAlert visibleUpdateAlert={visibleUpdateAlert} updateServiceWorker={updateServiceWorker} />
      )}

      <Modals />
      <Suspense
        fallback={
          <Splash
            message={
              <span>
                لطفا منتظر باشید
                <i className="fa fa-spinner animate-spin"></i>
              </span>
            }
          />
        }
      >
        <Switch>
          <Route exact path="/" component={(props) => <AuthLoading {...props} />} />
          <Route exact path="/transfer" component={(props) => <TransferBanner {...props} />} />
          <Route exact path="/login" component={(props) => <Login {...props} />} />
          <Route exact path="/forgot" component={(props) => <Forgot {...props} />} />
          <Route exact path="/forgot/verify" component={(props) => <ForgotVerify {...props} />} />
          <Route exact path="/forgot/reset" component={(props) => <ForgotReset {...props} />} />
          <Layouts exact hasTab path="/doctors/:doctorId(\d+)/dashboard" component={DashboardHome} />
          <Layouts exact hasTab path="/dashboard" component={Dashboard} />
          <Layouts exact path="/doctors/:doctorId(\d+)/editInfo" component={EditInfo} />
          <Layouts exact path="/doctors/:doctorId(\d+)/comments" component={Comments} />
          <Layouts exact path="/doctors/:doctorId(\d+)/comments/:status?" component={Comments} />
          <Layouts exact path="/doctors/:doctorId(\d+)/reports" component={Reports} />
          <Layouts exact hasNewTicket path="/doctors/:doctorId(\d+)/tickets" component={Tickets} />
          <Layouts exact path="/doctors/:doctorId(\d+)/tickets/:ticketId(\d+)" component={TicketDetail} />
          <Layouts exact path="/doctors/:doctorId(\d+)/tickets/new" component={NewTicket} />
          <Layouts exact path="/doctors/:doctorId(\d+)/bookings" component={Bookings} />
          <Layouts exact path="/doctors/:doctorId(\d+)/bookings/:bookingId?" component={Bookings} />
          <Layouts exact path="/doctors/:doctorId(\d+)/timeslots" component={Timeslots} />
          <Layouts exact path="/doctors/:doctorId(\d+)/schedule" component={Schedule} />
          <Layouts exact path="/doctors/:doctorId(\d+)/schedule/:scheduleId(\d+)" component={EditSchedule} />
          <Layouts exact path="/doctors/:doctorId(\d+)/schedule/new" component={NewSchedule} />
          <Layouts exact path="/doctors/:doctorId(\d+)/package/verify" component={PackageVerification} />
          <Layouts exact path="/doctors/:doctorId(\d+)/wallet/verify" component={BiddingVerification} />
          <Layouts exact path="/doctors/:doctorId(\d+)/package" component={Package} />
          <Layouts exact path="/doctors/:doctorId(\d+)/bidding" component={Bidding} />
          <Layouts exact path="/doctors/:doctorId(\d+)/wallet" component={Wallet} />
          <Layouts exact path="/doctors/:doctorId(\d+)/notification" component={NotificationList} />
          <Layouts exact path="/doctors/:doctorId(\d+)/ranks" component={Ranks} />

          <Layouts exact hasTab path="/offices/:id(\d+)/bookings/:status?" component={Bookings} />
          <Layouts exact path="/offices/:id(\d+)/profile" component={OfficeProfile} />
          <Layouts exact hasSelector={false} path="/offices" component={Offices} />
          <Layouts exact hasSelector={false} path="/doctors/:doctorId(\d+)/contact-us" component={ContactUs} />

          <Layouts exact hasSelector={false} path="/doctors/:doctorId(\d+)/offices" component={DoctorsOffice} />
          <Layouts
            exact
            hasSelector={false}
            path="/doctors/:doctorId(\d+)/offices/:officeId(\d+)"
            component={DoctorOfficeDetails}
          />
          <Layouts
            exact
            hasSelector={false}
            path="/doctors/:doctorId(\d+)/notification/:notificationId(\d+)"
            component={NotificationItem}
          />

          <Route exact path="/offline" component={Offline} />
          <Route exact path="/landing" component={Landing} />
          <Route exact path="*" component={NotFound} />
        </Switch>
      </Suspense>
    </Router>
  );
}

export default App;
